import DynamicButton from "../button/dynamicButton";
import { usePushNotificationSubscription } from "services/pushNotifications/hooks/usePushNotificationSubscription";
import {ReactComponent as NotificationBellActive } from "../../../content/icons/notifications_bell_active.svg";
import {ReactComponent as NotificationBellOff } from "../../../content/icons/notifications_bell_off.svg";
import "./pushNotificationButton.scss";
import classNames from "classnames";
import { useState } from "react";
import {debounce} from "lodash";
import { Localizer } from "infrastructure/localization/localizer";
import useUser from "core/hooks/useUser";
import { UserModel } from "services/account/userModel";

const getBellIcon = (notificationPermission?: NotificationPermission, hasSubscribed?: boolean) => {
  if (notificationPermission === "granted" && hasSubscribed) {
    return <NotificationBellActive className="bell-active large"/>;
  }
  return <NotificationBellOff className="bell-off large"/>;
}

const getButtonDescription = (currentUser: UserModel, notificationPermission?: NotificationPermission, hasSubscribed?: boolean) => {
  if (currentUser.IsImpersonating()) {
    return Localizer.pushNotifikationer_DisabledBecauseOfImpersonateDescription();
  }
  if (notificationPermission === undefined) {
    return undefined;
  }
  if (notificationPermission === "denied") {
    return Localizer.pushNotifikationer_BrowserNotificationsDeniedDescription();
  }
  if (notificationPermission === "granted" && hasSubscribed) {
    return Localizer.pushNotifikationer_PushNotificationsIsSubscribedDescription();
  }
  if (!hasSubscribed) {
    return Localizer.pushNotifikationer_PushNotificationsIsNotSubscribedDescription();
  }
}

export type PushNotificationButtonProps = {
  showDescriptionAsText?: boolean;
}

export const PushNotificationButton = ({
  showDescriptionAsText = false
}: PushNotificationButtonProps) => {

  const { 
    subscriptionState,
    requestNotificationPermission, 
    subscribe, 
    unsubscribe 
  } = usePushNotificationSubscription();

  const { currentUser } = useUser();
  const [isToggling, setIsToggling] = useState(false);

  const toggleNotifications = async () => {
    const canToggle = !isToggling && !currentUser.IsImpersonating();
    if(!canToggle) return;
    
    setIsToggling(true);

    try {
      const registration = await navigator.serviceWorker.getRegistration();
      if (!registration) {
        console.warn("ToggleNotifications - no registration was found.")
        return;
      }
  
      const updatedPermission = subscriptionState.notificationPermission !== 'granted' ? await requestNotificationPermission() : subscriptionState.notificationPermission;
      const subscription = await registration.pushManager.getSubscription();
      const subscriptionExists = !!subscription && !!subscriptionState.backendHasSubscription;
  
      const shouldSubscribe = updatedPermission === "granted" && !subscriptionExists;
      const shouldUnsubscribe = subscriptionExists;
  
      if(shouldSubscribe) {
        await subscribe(registration);
  
      } else if (shouldUnsubscribe) {
        await unsubscribe(registration);
  
      } else {
        console.warn(`Didn't subcribe/unsubscribe - updatedPermission: ${updatedPermission}, hasSubscription: ${(!!subscription)}`)
      }
    } finally {
      setIsToggling(false);
    }
  };

  const debouncedOnClick = debounce(toggleNotifications, 200, { maxWait: 2000 });

  const notificationsIsDenied = subscriptionState.notificationPermission === 'denied';
  const buttonIsDisabled = currentUser.IsImpersonating() || notificationsIsDenied || isToggling;
  const subscriptionIsActive = subscriptionState.deviceHasSubscription && subscriptionState.backendHasSubscription;

  const buttonDescription = getButtonDescription(currentUser, subscriptionState.notificationPermission, subscriptionIsActive);

  return (
    <div>
      <div className="d-flex flex-align-c">
        <DynamicButton
          additionalClasses={classNames("push-notification-button")}
          disabled={buttonIsDisabled}
          onClick={debouncedOnClick}
          type="button"
          rounded
          isIconButton
          title={!showDescriptionAsText ? buttonDescription : undefined}
        >
          {getBellIcon(subscriptionState.notificationPermission, subscriptionIsActive)}
        </DynamicButton>
        {showDescriptionAsText && 
          <div className={classNames("margin-left-m small", buttonIsDisabled && "subtle")}>{buttonDescription}</div>
        }
      </div>
    </div>
  );
}
